import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import classNames from 'classnames';
import { Form } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { useTranslation } from 'react-i18next';
import { Loader } from 'ncoded-component-library';
import ListingContext from '../../providers/Listing/Listing.context';
import StepsContext from 'providers/Steps/Steps.context';
import { useLocation } from 'react-router-dom';
import utils, {
  checkIsUrgent,
  scrollToErrorsOnSubmit,
  sentryError,
} from 'utils';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { SUCCESS_MESSAGES } from 'request-responses';
import FloatingActions from 'components/FloatingActions';
import AuthContext from 'providers/Auth/Auth.context';
import {
  HIDE_CONTROLS_PAGES,
  pickCarriedValues,
  processLCWValuesBeforeSubmit,
  validateLCW,
} from 'router/subrouters/ListingCreationWizard/utils';
import { Listing } from 'models/Listing';
import AiDraftedFieldsTracker from 'router/subrouters/ListingCreationWizard/components/AiDraftedFieldsTracker';

import './ListingEditStepWrapper.styles.scss';

type ListingEditStepWrapperProps = {
  className?: string;
};

const ListingEditStepWrapper: React.FC<ListingEditStepWrapperProps> = (
  props,
) => {
  const { children, className } = props;

  const { popServerError, popSuccess } = useContext(PopNotificationsContext);
  const { navigateBack } = useContext(AuthContext);
  const { nextStep, currentStep, steps, prevStep } = useContext(StepsContext);
  const {
    patchListing,
    listingFormValues,
    setListing,
    listing: { type: listingType },
  } = useContext(ListingContext);

  const valuesRef = useRef<Partial<Listing>>(null);

  /**
   * Edit listing saves only on Save/Next - patching per field would write to a
   * live listing, and Statement of Information has fields that only make sense
   * together. Merging the current values into the local draft on the way out
   * keeps them visible when switching to preview mode without sending
   * anything; they still reach the server on Save.
   */
  useEffect(
    () => () => {
      if (!valuesRef.current) return;

      setListing((old) => ({
        ...old,
        ...pickCarriedValues(valuesRef.current),
      }));
    },
    [setListing],
  );

  const isLastStep = useMemo(
    () => currentStep + 1 === steps.length,
    [currentStep, steps.length],
  );

  const classes = classNames('listing-edit-step-wrapper', className);

  const { pathname } = useLocation();

  const { t } = useTranslation();

  // PATCH LISTING : EDIT LISTING
  const patchListingSubmit = useCallback(
    async (values) => {
      const { appointmentType, ...restValues } = values;

      const processedValues = processLCWValuesBeforeSubmit(restValues);

      const patchValues = utils.changePropertyValueWith(
        {
          ...restValues,
          ...processedValues,
          ...(currentStep === 0 && checkIsUrgent(listingType)
            ? { appointmentType }
            : {}),
        },
        null,
        undefined,
      );

      try {
        await patchListing(patchValues);
        isLastStep ? navigateBack() : nextStep({ replace: true });
        popSuccess(t(SUCCESS_MESSAGES.PACHED_LISTING));
      } catch (e) {
        sentryError(e);
        popServerError(e);
      }
    },
    [
      currentStep,
      isLastStep,
      listingType,
      navigateBack,
      nextStep,
      patchListing,
      popServerError,
      popSuccess,
      t,
    ],
  );

  const hideControls = useMemo(
    () => HIDE_CONTROLS_PAGES.some((el) => pathname.includes(el)),
    [pathname],
  );

  return (
    <Form
      keepDirtyOnReinitialize
      onSubmit={patchListingSubmit}
      initialValues={listingFormValues}
      mutators={{ ...arrayMutators }}
      validate={validateLCW}
      render={({
        submitting,
        invalid,
        values,
        form: { submit },
        handleSubmit,
      }) => {
        valuesRef.current = values;

        return (
          <form onSubmit={handleSubmit} className={classes}>
            {submitting && <Loader />}

            <AiDraftedFieldsTracker />

            {children}

            {!hideControls && (
              <FloatingActions
                disabled={submitting}
                message={t(
                  invalid ? 'formHasMissingFields' : 'formReadyToSubmit',
                )}
                cancelTxt={t('back')}
                submitTxt={t(isLastStep ? 'save' : 'next')}
                onSubmit={() => scrollToErrorsOnSubmit(invalid, submit)}
                {...(currentStep !== 0 && {
                  onCancel: () => prevStep({ replace: true }),
                })}
              />
            )}
          </form>
        );
      }}
    />
  );
};

export default ListingEditStepWrapper;
